import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/components/ProtectedRoute";
import api, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { AdBanner } from "@/components/AdSense";
import ProGate from "@/components/ProGate";
import {
  Plus,
  Trash2,
  Copy,
  ExternalLink,
  RefreshCw,
  CreditCard,
  RectangleHorizontal,
  Layers,
  Check,
  ChevronDown,
  Search,
  X,
  User,
  Gamepad2,
  Star,
  Palette,
  EyeOff,
  Pencil,
  CircleUser,
} from "lucide-react";

const API = `${getApiUrl()}/api`;

const STYLE_ICONS = {
  card: CreditCard,
  banner: RectangleHorizontal,
  playing_card: Layers,
};

const THEME_COLORS = {
  dark: "#0A0A0A",
  light: "#D0D3D8",
  xbox: "#107C10",
  playstation: "#003087",
  steam: "#66C0F4",
  retro: "#DC9B1E",
  neon: "#FF008C",
  custom: "#7C3AED",
};

const PLATFORM_OPTIONS = [
  { id: "xbox", label: "Xbox", color: "#107C10" },
  { id: "playstation", label: "PlayStation", color: "#003087" },
  { id: "steam", label: "Steam", color: "#66C0F4" },
];

const SCOPE_TYPES_PROFILE = [
  { id: "single_platform", label: "Single Platform" },
  { id: "multi_platform", label: "Select Platforms" },
  { id: "all_platforms", label: "All Platforms" },
];

export default function SignatureGenerator() {
  const { user } = useAuth();
  const [options, setOptions] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showSnippets, setShowSnippets] = useState(null);
  const [editingCardId, setEditingCardId] = useState(null); // null = create mode, string = editing

  // Card type toggle
  const [cardType, setCardType] = useState("profile"); // profile | game

  // Form state
  const [form, setForm] = useState({
    name: "",
    style: "card",
    theme: "dark",
    scopeType: "all_platforms",
    platforms: ["xbox", "playstation", "steam"],
    selectedStats: ["total_achievements", "gamerscore", "completion_pct", "game_count", "completed_games", "best_games", "recent_achievements", "streak", "best_streak", "custom_text", "box_art", "favorite_achievements"],
    customText: "",
    holidayTheme: false,
    holidayPreview: "",
    accentColor: "#7C3AED",
    bgColor: "#0A0A0A",
    transparentBg: true,
    showPlatformAvatars: true,
    font: "default",
    visualEffects: [],
    completionDisplay: "bar",
    glowColor: "",
    borderStyle: "none",
    showNetworkBg: false,
  });

  // Game card state
  const [gameSearch, setGameSearch] = useState("");
  const [gameResults, setGameResults] = useState([]);
  const [selectedGames, setSelectedGames] = useState([]);
  const [searchingGames, setSearchingGames] = useState(false);

  // Favorite achievements
  const [gameAchievements, setGameAchievements] = useState([]);
  const [favAchievementIds, setFavAchievementIds] = useState([]);
  const [includeDlc, setIncludeDlc] = useState(false);
  const [gameDlc, setGameDlc] = useState([]);
  const [loadingAchs, setLoadingAchs] = useState(false);
  const [useSampleData, setUseSampleData] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [optRes, cardsRes] = await Promise.all([
        api.get(`${API}/signatures/options`),
        api.get(`${API}/signatures/cards`),
      ]);
      setOptions(optRes.data);
      setCards(cardsRes.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Game search with debounce
  useEffect(() => {
    if (cardType !== "game") return;
    const timeout = setTimeout(async () => {
      setSearchingGames(true);
      try {
        const { data } = await api.get(`${API}/signatures/games-search?q=${encodeURIComponent(gameSearch)}`);
        setGameResults(data);
      } catch {
        // silent
      } finally {
        setSearchingGames(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [gameSearch, cardType]);

  // Load achievements when single game selected for favorites
  useEffect(() => {
    if (selectedGames.length === 1 && form.selectedStats.includes("favorite_achievements")) {
      setLoadingAchs(true);
      api.get(`${API}/signatures/game-achievements/${selectedGames[0].id}`)
        .then(({ data }) => setGameAchievements(data))
        .catch(() => setGameAchievements([]))
        .finally(() => setLoadingAchs(false));
    } else {
      setGameAchievements([]);
      setFavAchievementIds([]);
    }
  }, [selectedGames, form.selectedStats]);

  // Load DLC when a game is selected
  useEffect(() => {
    if (selectedGames.length === 1) {
      api.get(`${API}/signatures/game-dlc/${selectedGames[0].id}`)
        .then(({ data }) => setGameDlc(data))
        .catch(() => setGameDlc([]));
    } else {
      setGameDlc([]);
      setIncludeDlc(false);
    }
  }, [selectedGames]);


  // Generate preview
  const generatePreview = useCallback(async () => {
    if (cardType === "profile") {
      const params = new URLSearchParams({
        style: form.style,
        theme: form.theme,
        scope_type: form.scopeType,
        platforms: form.platforms.join(","),
        stats_list: form.selectedStats.join(","),
        custom_text: form.customText,
        holiday_theme: (form.holidayTheme || !!form.holidayPreview).toString(),
        transparent_bg: form.transparentBg.toString(),
        show_platform_avatars: form.showPlatformAvatars.toString(),
        completion_display: form.completionDisplay,
        ...(form.glowColor ? { glow_color: form.glowColor } : {}),
        ...(form.holidayPreview ? { holiday_preview: form.holidayPreview } : {}),
        font: form.font,
        visual_effects: form.visualEffects.join(","),
        border_style: form.borderStyle,
        show_network_bg: form.showNetworkBg.toString(),
        sample: useSampleData.toString(),
      });
      if (form.theme === "custom") {
        params.set("accent_color", form.accentColor);
        params.set("bg_color", form.bgColor);
      }
      setPreviewUrl(`${API}/signatures/preview?${params.toString()}&_t=${Date.now()}`);
    } else if (useSampleData || selectedGames.length > 0) {
      // Use the new game card preview endpoint
      try {
        const payload = {
          style: form.style,
          theme: form.theme,
          gameIds: useSampleData ? [] : selectedGames.map((g) => g.id),
          selectedStats: form.selectedStats,
          customText: form.customText,
          holidayTheme: form.holidayTheme,
          transparentBg: form.transparentBg,
          showPlatformAvatars: form.showPlatformAvatars,
          completionDisplay: form.completionDisplay,
          glowColor: form.glowColor,
          favoriteAchievementIds: useSampleData ? ["sample"] : favAchievementIds,
          font: form.font,
          visualEffects: form.visualEffects,
          borderStyle: form.borderStyle,
          showNetworkBg: form.showNetworkBg,
          includeDlc: includeDlc,
          sample: useSampleData,
        };
        if (form.theme === "custom") {
          payload.accentColor = form.accentColor;
          payload.bgColor = form.bgColor;
        }
        const resp = await api.post(`${API}/signatures/preview-game`, payload, {
          responseType: "blob",
        });
        const url = URL.createObjectURL(resp.data);
        setPreviewUrl(url);
      } catch {
        setPreviewUrl(null);
      }
    } else {
      setPreviewUrl(null);
    }
  }, [form, cardType, selectedGames, favAchievementIds, includeDlc, useSampleData]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (options) generatePreview();
    }, 500);
    return () => clearTimeout(timeout);
  }, [form, options, generatePreview]);

  const handleCreate = async () => {
    if (creating) return;

    if (cardType === "game" && selectedGames.length === 0) {
      toast.error("Please select at least one game");
      return;
    }

    setCreating(true);
    try {
      const scopeType = cardType === "game"
        ? (selectedGames.length > 1 ? "multi_game" : "single_game")
        : form.scopeType;

      const payload = {
        cardType,
        style: form.style,
        theme: form.theme,
        scope: {
          type: scopeType,
          platforms: cardType === "profile" ? form.platforms : null,
          gameIds: cardType === "game" ? selectedGames.map((g) => g.id) : null,
        },
        selectedStats: form.selectedStats,
        customText: form.customText || null,
        holidayTheme: form.holidayTheme,
        favoriteAchievementIds: cardType === "game" ? favAchievementIds : null,
        customColors: form.theme === "custom" ? { accent: form.accentColor, background: form.bgColor } : null,
        transparentBg: form.transparentBg,
        showPlatformAvatars: form.showPlatformAvatars,
        font: form.font,
        visualEffects: form.visualEffects,
        borderStyle: form.borderStyle,
        showNetworkBg: form.showNetworkBg,
        name: form.name || (
          cardType === "game"
            ? (selectedGames.length === 1
                ? `${selectedGames[0].name}${form.style === "banner" ? " Banner" : ""}`
                : `${selectedGames.length} Games`)
            : `${form.style === "card" ? "Card" : form.style === "banner" ? "Banner" : "Playing Card"} - ${form.theme}`
        ),
      };
      const { data } = await api.post(`${API}/signatures/cards`, payload);
      toast.success("Signature card created!");
      setCards((prev) => [data, ...prev]);
      setEditingCardId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create card");
    } finally {
      setCreating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (creating || !editingCardId) return;
    setCreating(true);
    try {
      const payload = {
        style: form.style,
        theme: form.theme,
        scope: {
          type: cardType === "game"
            ? (selectedGames.length > 1 ? "multi_game" : "single_game")
            : form.scopeType,
          platforms: cardType === "profile" ? form.platforms : null,
          gameIds: cardType === "game" ? selectedGames.map((g) => g.id) : null,
        },
        selectedStats: form.selectedStats,
        customText: form.customText || null,
        holidayTheme: form.holidayTheme,
        favoriteAchievementIds: cardType === "game" ? favAchievementIds : null,
        customColors: form.theme === "custom" ? { accent: form.accentColor, background: form.bgColor } : null,
        transparentBg: form.transparentBg,
        showPlatformAvatars: form.showPlatformAvatars,
        font: form.font,
        visualEffects: form.visualEffects,
        borderStyle: form.borderStyle,
        showNetworkBg: form.showNetworkBg,
        name: form.name || (
          cardType === "game" && selectedGames.length === 1
            ? `${selectedGames[0].name}${form.style === "banner" ? " Banner" : ""}`
            : null
        ),
      };
      const { data } = await api.put(`${API}/signatures/cards/${editingCardId}`, payload);
      toast.success("Card updated!");
      setCards((prev) => prev.map((c) => (c.id === editingCardId ? data : c)));
      setEditingCardId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update card");
    } finally {
      setCreating(false);
    }
  };

  const editCard = (card) => {
    setEditingCardId(card.id);
    const scope = card.scope || {};
    setCardType(card.cardType || "profile");
    setForm({
      name: card.name || "",
      style: card.style || "card",
      theme: card.theme || "dark",
      scopeType: scope.type || "all_platforms",
      platforms: scope.platforms || ["xbox", "playstation", "steam"],
      selectedStats: card.selectedStats || ["total_achievements", "gamerscore", "completion_pct", "game_count", "completed_games", "best_games", "recent_achievements", "streak", "best_streak", "custom_text", "box_art", "favorite_achievements"],
      customText: card.customText || "",
      holidayTheme: card.holidayTheme || false,
      accentColor: card.customColors?.accent || "#7C3AED",
      bgColor: card.customColors?.background || "#0A0A0A",
      transparentBg: card.transparentBg || false,
      showPlatformAvatars: card.showPlatformAvatars !== false,
      font: card.font || "default",
      visualEffects: card.visualEffects || [],
      borderStyle: card.borderStyle || "none",
      showNetworkBg: card.showNetworkBg || false,
    });
    // If game card, load selected games
    if (card.cardType === "game" && scope.gameIds?.length) {
      api.get(`${API}/signatures/games-search?q=`)
        .then(({ data: allGames }) => {
          const matched = scope.gameIds
            .map((gid) => allGames.find((g) => g.id === gid))
            .filter(Boolean);
          setSelectedGames(matched.length > 0 ? matched : []);
        })
        .catch(() => setSelectedGames([]));
    } else {
      setSelectedGames([]);
    }
    setFavAchievementIds(card.favoriteAchievementIds || []);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingCardId(null);
    setForm({
      name: "",
      style: "card",
      theme: "dark",
      scopeType: "all_platforms",
      platforms: ["xbox", "playstation", "steam"],
      selectedStats: ["total_achievements", "gamerscore", "completion_pct", "game_count", "completed_games", "best_games", "recent_achievements", "streak", "best_streak", "custom_text", "box_art", "favorite_achievements"],
      customText: "",
      holidayTheme: false,
      holidayPreview: "",
      accentColor: "#7C3AED",
      bgColor: "#0A0A0A",
      transparentBg: true,
      showPlatformAvatars: true,
      font: "default",
      visualEffects: [],
      borderStyle: "none",
    });
    setSelectedGames([]);
    setFavAchievementIds([]);
  };

  const handleDelete = async (cardId) => {
    try {
      await api.delete(`${API}/signatures/cards/${cardId}`);
      toast.success("Card deleted");
      setCards((prev) => prev.filter((c) => c.id !== cardId));
    } catch {
      toast.error("Failed to delete");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const toggleStat = (statId) => {
    setForm((prev) => ({
      ...prev,
      selectedStats: prev.selectedStats.includes(statId)
        ? prev.selectedStats.filter((s) => s !== statId)
        : [...prev.selectedStats, statId],
    }));
  };

  const togglePlatform = (platId) => {
    setForm((prev) => {
      const has = prev.platforms.includes(platId);
      const next = has ? prev.platforms.filter((p) => p !== platId) : [...prev.platforms, platId];
      return { ...prev, platforms: next.length > 0 ? next : prev.platforms };
    });
  };

  const addGame = (game) => {
    const maxGames = form.style === "banner" ? 1 : 5;
    if (selectedGames.length >= maxGames) {
      toast.error(form.style === "banner" ? "Banner supports 1 game only" : "Maximum 5 games");
      return;
    }
    if (selectedGames.find((g) => g.id === game.id)) return;
    setSelectedGames((prev) => [...prev, game]);
  };

  const removeGame = (gameId) => {
    setSelectedGames((prev) => prev.filter((g) => g.id !== gameId));
  };

  const toggleFavAchievement = (achId) => {
    const maxFav = form.style === "banner" ? 1 : 4;
    setFavAchievementIds((prev) =>
      prev.includes(achId) ? prev.filter((id) => id !== achId) : prev.length < maxFav ? [...prev, achId] : prev
    );
  };

  // Filter stat options based on card type
  const filteredStatOptions = (options?.statOptions || []).filter((so) => {
    if (cardType === "profile") return !so.gameOnly;
    if (cardType === "game") {
      if (so.profileOnly) return false;
      // Hide custom_text for banner style
      if (form.style === "banner" && so.key === "custom_text") return false;
      return true;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#7C3AED]" />
      </div>
    );
  }

  const profileCount = cards.filter((c) => c.cardType === "profile").length;
  const maxProfileReached = profileCount >= (options?.limits?.maxProfileCards || 5);

  return (
    <ProGate feature="signatures">
    <div className="space-y-6" data-testid="signature-generator-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-2xl text-white" data-testid="signature-page-title">
          Signature Generator
        </h1>
        <p className="text-white/50 text-sm mt-1">
          Create dynamic stat images for forum signatures, social posts, and more.
        </p>
      </div>

      {/* Card Type Toggle */}
      <div className="flex gap-2" data-testid="card-type-toggle">
        <button
          onClick={() => setCardType("profile")}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${
            cardType === "profile"
              ? "border-[#7C3AED] bg-[#7C3AED]/10 text-white"
              : "border-white/10 text-white/50 hover:border-white/20"
          }`}
          data-testid="card-type-profile"
        >
          <User className="w-4 h-4" /> Profile Card
        </button>
        <button
          onClick={() => setCardType("game")}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${
            cardType === "game"
              ? "border-[#7C3AED] bg-[#7C3AED]/10 text-white"
              : "border-white/10 text-white/50 hover:border-white/20"
          }`}
          data-testid="card-type-game"
        >
          <Gamepad2 className="w-4 h-4" /> Game Card
        </button>
      </div>

      {/* Style Selection (compact, inline) */}
      <div className="flex items-center gap-2" data-testid="style-selector">
        <span className="text-white/40 text-xs font-medium mr-1">Style</span>
        {options?.styles?.map((s) => {
          const Icon = STYLE_ICONS[s.id] || CreditCard;
          const active = form.style === s.id;
          return (
            <button
              key={s.id}
              onClick={() => {
                setForm((p) => ({ ...p, style: s.id }));
                if (s.id === "banner" && selectedGames.length > 1) {
                  setSelectedGames((prev) => [prev[0]]);
                }
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-all ${
                active
                  ? "border-[#7C3AED] bg-[#7C3AED]/10 text-white"
                  : "border-white/10 text-white/40 hover:border-white/20 hover:text-white/60"
              }`}
              data-testid={`style-btn-${s.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {s.name}
              <span className="text-[10px] text-white/25">{s.dimensions}</span>
            </button>
          );
        })}
      </div>

      {/* Edit Mode Banner */}
      {editingCardId && (
        <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="edit-mode-banner">
          <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
            <Pencil className="w-4 h-4" />
            Editing card: {cards.find((c) => c.id === editingCardId)?.name || editingCardId}
          </div>
          <button
            onClick={cancelEdit}
            className="text-xs text-white/50 hover:text-white border border-white/10 px-3 py-1 rounded"
            data-testid="cancel-edit-btn"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Left: Configuration */}
        <div className="space-y-5">
          {/* Card Name */}
          <div>
            <label className="text-white/60 text-xs font-medium block mb-1.5">Card Name (optional)</label>
            <Input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder={cardType === "game" ? "My Game Signature" : "My Profile Signature"}
              className="bg-black/30 border-white/10 text-white text-sm"
              data-testid="card-name-input"
            />
          </div>

          {/* Game Selection (for game cards) */}
          {cardType === "game" && (
            <div data-testid="game-selector">
              <label className="text-white/60 text-xs font-medium block mb-2">
                {form.style === "banner" ? "Select Game" : "Select Games (up to 5)"}
              </label>

              {/* Selected games */}
              {selectedGames.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {selectedGames.map((g) => (
                    <div
                      key={g.id}
                      className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#7C3AED]/10 border border-[#7C3AED]/30 text-xs text-white"
                      data-testid={`selected-game-${g.id}`}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PLATFORM_OPTIONS.find(p => p.id === g.platform)?.color || "#666" }} />
                      <span className="truncate max-w-[160px]">{g.name}</span>
                      <button onClick={() => removeGame(g.id)} className="text-white/40 hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
                <Input
                  value={gameSearch}
                  onChange={(e) => setGameSearch(e.target.value)}
                  placeholder="Search your games..."
                  className="bg-black/30 border-white/10 text-white text-sm pl-8"
                  data-testid="game-search-input"
                />
              </div>

              {/* Results */}
              {gameResults.length > 0 && (
                <div className="mt-2 max-h-[200px] overflow-y-auto border border-white/5 rounded-lg bg-black/30">
                  {gameResults.filter(g => !selectedGames.find(sg => sg.id === g.id)).slice(0, 10).map((game) => (
                    <button
                      key={game.id}
                      onClick={() => addGame(game)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                      data-testid={`game-result-${game.id}`}
                    >
                      {game.boxArtUrl && (
                        <img src={game.boxArtUrl} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-white truncate">{game.name}</div>
                        <div className="text-[10px] text-white/40 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: PLATFORM_OPTIONS.find(p => p.id === game.platform)?.color || "#666" }} />
                          {game.platform} | {game.unlockedAchievements || 0}/{game.totalAchievements || 0}
                        </div>
                      </div>
                      <Plus className="w-3.5 h-3.5 text-white/30 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Theme Selection */}
          <div>
            <label className="text-white/60 text-xs font-medium block mb-2">Theme</label>
            <div className="flex flex-wrap gap-2">
              {options?.themes?.map((t) => {
                const active = form.theme === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setForm((p) => ({ ...p, theme: t.id }))}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-all ${
                      active
                        ? "border-[#7C3AED] bg-[#7C3AED]/10 text-white"
                        : "border-white/10 bg-black/20 text-white/50 hover:border-white/20"
                    }`}
                    data-testid={`theme-btn-${t.id}`}
                  >
                    {t.id === "custom" ? (
                      <Palette className="w-3 h-3" />
                    ) : (
                      <div
                        className="w-3 h-3 rounded-full border border-white/20"
                        style={{ backgroundColor: THEME_COLORS[t.id] || "#333" }}
                      />
                    )}
                    {t.name}
                    {active && <Check className="w-3 h-3 text-[#7C3AED]" />}
                  </button>
                );
              })}
            </div>

            {/* Custom Color Pickers */}
            {form.theme === "custom" && (
              <div className="mt-3 flex gap-4" data-testid="custom-color-pickers">
                <div className="flex-1">
                  <label className="text-white/40 text-[10px] block mb-1">Accent Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.accentColor}
                      onChange={(e) => setForm((p) => ({ ...p, accentColor: e.target.value }))}
                      className="w-8 h-8 rounded cursor-pointer border border-white/10 bg-transparent"
                      data-testid="accent-color-picker"
                    />
                    <Input
                      value={form.accentColor}
                      onChange={(e) => setForm((p) => ({ ...p, accentColor: e.target.value }))}
                      className="bg-black/30 border-white/10 text-white text-xs font-mono h-8 w-24"
                      maxLength={7}
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <label className="text-white/40 text-[10px] block mb-1">Background Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.bgColor}
                      onChange={(e) => setForm((p) => ({ ...p, bgColor: e.target.value }))}
                      className="w-8 h-8 rounded cursor-pointer border border-white/10 bg-transparent"
                      data-testid="bg-color-picker"
                    />
                    <Input
                      value={form.bgColor}
                      onChange={(e) => setForm((p) => ({ ...p, bgColor: e.target.value }))}
                      className="bg-black/30 border-white/10 text-white text-xs font-mono h-8 w-24"
                      maxLength={7}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Background Transparency */}
          <label className="flex items-center gap-2 cursor-pointer" data-testid="transparent-bg-toggle">
            <Checkbox
              checked={form.transparentBg}
              onCheckedChange={(v) => setForm((p) => ({ ...p, transparentBg: !!v }))}
              className="w-4 h-4"
              data-testid="transparent-bg-check"
            />
            <EyeOff className="w-3.5 h-3.5 text-white/40" />
            <span className="text-sm text-white/60">Transparent background</span>
          </label>

          {/* Holiday Theme */}
          <label className="flex items-center gap-2 cursor-pointer" data-testid="holiday-theme-toggle">
            <Checkbox
              checked={form.holidayTheme}
              onCheckedChange={(v) => setForm((p) => ({ ...p, holidayTheme: !!v }))}
              className="w-4 h-4"
              data-testid="holiday-theme-check"
            />
            <span className="text-sm text-white/60">Enable holiday themes</span>
          </label>
          <div>
            <label className="text-white/60 text-xs font-medium block mb-2">Holiday Theme Preview</label>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, holidayTheme: false, holidayPreview: "" }))}
                className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                  !form.holidayPreview && !form.holidayTheme
                    ? "bg-white/10 border-white/30 text-white"
                    : "border-white/10 text-white/40 hover:border-white/20"
                }`}
                data-testid="holiday-none-btn"
              >
                None
              </button>
              {(options?.holidays || []).map((h) => {
                const active = form.holidayPreview === h;
                const labels = {
                  christmas: "Christmas",
                  halloween: "Halloween",
                  new_year: "New Year",
                  valentines: "Valentine's",
                  st_patricks: "St. Patrick's",
                  independence_day: "4th of July",
                  easter: "Easter",
                };
                const colors = {
                  christmas: "text-red-400 border-red-500/50",
                  halloween: "text-orange-400 border-orange-500/50",
                  new_year: "text-yellow-400 border-yellow-500/50",
                  valentines: "text-pink-400 border-pink-500/50",
                  st_patricks: "text-green-400 border-green-500/50",
                  independence_day: "text-blue-400 border-blue-500/50",
                  easter: "text-purple-400 border-purple-500/50",
                };
                return (
                  <button
                    key={h}
                    type="button"
                    onClick={() =>
                      setForm((p) => ({
                        ...p,
                        holidayTheme: true,
                        holidayPreview: active ? "" : h,
                        ...(active ? { holidayTheme: false } : {}),
                      }))
                    }
                    className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                      active
                        ? `bg-white/10 ${colors[h] || "text-white border-white/30"}`
                        : "border-white/10 text-white/40 hover:border-white/20"
                    }`}
                    data-testid={`holiday-${h}-btn`}
                  >
                    {labels[h] || h.replace(/_/g, " ")}
                  </button>
                );
              })}
            </div>
            {options?.activeHoliday && (
              <div className="mt-1.5 text-[11px] text-amber-400/70" data-testid="active-holiday-badge">
                Currently active: {options.activeHoliday.replace(/_/g, " ")}
              </div>
            )}
          </div>

          {/* Show Platform Avatars */}
          {form.scopeType !== "single_platform" && (
            <div>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="platform-avatars-toggle">
                <Checkbox
                  checked={form.showPlatformAvatars}
                  onCheckedChange={(v) => setForm((p) => ({ ...p, showPlatformAvatars: !!v }))}
                  className="w-4 h-4"
                  data-testid="platform-avatars-check"
                />
                <CircleUser className="w-3.5 h-3.5 text-white/40" />
                <span className="text-sm text-white/60">Show platform profile pictures</span>
              </label>
            </div>
          )}

          {/* Completion Display Style */}
          {form.selectedStats.includes("completion_pct") && !(cardType === "game" && form.style === "banner") && (
            <div>
              <label className="text-white/60 text-xs font-medium block mb-2">Completion Display</label>
              <div className="flex gap-2">
                {[
                  { id: "bar", label: "Health Bar" },
                  { id: "ring", label: "Ring" },
                ].map((opt) => {
                  const active = form.completionDisplay === opt.id;
                  return (
                    <button
                      key={opt.id}
                      data-testid={`completion-display-${opt.id}`}
                      onClick={() => setForm((p) => ({ ...p, completionDisplay: opt.id }))}
                      className={`px-3 py-1.5 rounded text-xs transition-all ${
                        active
                          ? "bg-white/15 text-white border border-white/30"
                          : "bg-white/5 text-white/50 border border-white/10 hover:bg-white/10"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}


          {/* Font Selection */}
          <div>
            <label className="text-white/60 text-xs font-medium block mb-2">Font Style</label>
            <div className="flex flex-wrap gap-2">
              {(options?.fonts || []).map((f) => {
                const active = form.font === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setForm((p) => ({ ...p, font: f.id }))}
                    className={`px-3 py-1.5 rounded-md border text-xs transition-all ${
                      active
                        ? "border-[#7C3AED] bg-[#7C3AED]/10 text-white"
                        : "border-white/10 text-white/50 hover:border-white/20"
                    }`}
                    data-testid={`font-btn-${f.id}`}
                    title={f.description}
                  >
                    {f.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Visual Effects */}
          <div>
            <label className="text-white/60 text-xs font-medium block mb-2">Visual Effects</label>
            <div className="grid grid-cols-2 gap-2">
              {(options?.visualEffects || []).map((fx) => {
                const active = form.visualEffects.includes(fx.id);
                return (
                  <button
                    key={fx.id}
                    onClick={() => {
                      setForm((p) => ({
                        ...p,
                        visualEffects: active
                          ? p.visualEffects.filter((e) => e !== fx.id)
                          : [...p.visualEffects, fx.id],
                      }));
                    }}
                    className={`px-3 py-2 rounded-md border text-xs transition-all text-left ${
                      active
                        ? "border-[#7C3AED] bg-[#7C3AED]/10 text-white"
                        : "border-white/10 text-white/50 hover:border-white/20"
                    }`}
                    data-testid={`fx-btn-${fx.id}`}
                  >
                    <span className="font-medium">{fx.name}</span>
                    <span className="block text-[10px] text-white/30 mt-0.5">{fx.description}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Glow Color (shown when glow effect is active) */}
          {form.visualEffects.includes("glow") && (
            <div>
              <label className="text-white/60 text-xs font-medium block mb-2">Glow Color</label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  data-testid="glow-color-picker"
                  value={form.glowColor || "#7C3AED"}
                  onChange={(e) => setForm((p) => ({ ...p, glowColor: e.target.value }))}
                  className="w-8 h-8 rounded border border-white/20 bg-transparent cursor-pointer"
                />
                <span className="text-white/40 text-xs">{form.glowColor || "Theme default"}</span>
                <button
                  onClick={() => setForm((p) => ({ ...p, glowColor: "" }))}
                  className="text-white/30 text-xs hover:text-white/60 ml-auto"
                >
                  Reset
                </button>
              </div>
            </div>
          )}

          {/* Platform Scope (profile only) */}
          {cardType === "profile" && (
            <div>
              <label className="text-white/60 text-xs font-medium block mb-2">Platform Scope</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {SCOPE_TYPES_PROFILE.map((st) => {
                  const active = form.scopeType === st.id;
                  return (
                    <button
                      key={st.id}
                      onClick={() => setForm((p) => ({ ...p, scopeType: st.id }))}
                      className={`px-3 py-1.5 rounded-md border text-xs transition-all ${
                        active
                          ? "border-[#7C3AED] bg-[#7C3AED]/10 text-white"
                          : "border-white/10 text-white/50 hover:border-white/20"
                      }`}
                      data-testid={`scope-btn-${st.id}`}
                    >
                      {st.label}
                    </button>
                  );
                })}
              </div>

              {form.scopeType !== "all_platforms" && (
                <div className="flex gap-2">
                  {PLATFORM_OPTIONS.map((p) => {
                    const checked = form.platforms.includes(p.id);
                    const isSingle = form.scopeType === "single_platform";
                    return (
                      <button
                        key={p.id}
                        onClick={() => {
                          if (isSingle) {
                            setForm((prev) => ({ ...prev, platforms: [p.id] }));
                          } else {
                            togglePlatform(p.id);
                          }
                        }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs transition-all ${
                          checked
                            ? "border-white/30 bg-white/5 text-white"
                            : "border-white/10 text-white/30 hover:text-white/50"
                        }`}
                        data-testid={`platform-btn-${p.id}`}
                      >
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Stats Selection */}
          <div>
            <label className="text-white/60 text-xs font-medium block mb-2">Stats to Display</label>
            <div className="space-y-1.5">
              {filteredStatOptions.map((so) => {
                const checked = form.selectedStats.includes(so.id);
                return (
                  <label key={so.id} className="flex items-center gap-2 cursor-pointer group">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleStat(so.id)}
                      className="w-4 h-4"
                      data-testid={`stat-check-${so.id}`}
                    />
                    <span className={`text-sm ${checked ? "text-white" : "text-white/40"} group-hover:text-white/70 transition-colors`}>
                      {so.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Favorite Achievements (single game only) */}
          {cardType === "game" && selectedGames.length === 1 && form.selectedStats.includes("favorite_achievements") && (
            <div data-testid="favorite-achievements-section">
              <label className="text-white/60 text-xs font-medium block mb-2">
                Favorite Achievements (up to {form.style === "banner" ? 1 : 4})
                {favAchievementIds.length > 0 && (
                  <span className="text-[#7C3AED] ml-1">({favAchievementIds.length}/{form.style === "banner" ? 1 : 4} selected)</span>
                )}
              </label>
              {loadingAchs ? (
                <div className="text-white/30 text-xs py-2">Loading achievements...</div>
              ) : gameAchievements.length === 0 ? (
                <div className="text-white/30 text-xs py-2">No unlocked achievements found</div>
              ) : (
                <div className="max-h-[200px] overflow-y-auto border border-white/5 rounded-lg bg-black/30">
                  {gameAchievements.map((ach) => {
                    const selected = favAchievementIds.includes(ach.id);
                    return (
                      <button
                        key={ach.id}
                        onClick={() => toggleFavAchievement(ach.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left border-b border-white/5 last:border-0 transition-colors ${
                          selected ? "bg-[#7C3AED]/10" : "hover:bg-white/5"
                        }`}
                        data-testid={`fav-ach-${ach.id}`}
                      >
                        {ach.imageUrl && (
                          <img src={ach.imageUrl} alt="" className="w-6 h-6 rounded shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-white truncate">{ach.name}</div>
                          {ach.score > 0 && <div className="text-[10px] text-white/40">{ach.score} pts</div>}
                        </div>
                        {selected ? (
                          <Star className="w-3.5 h-3.5 text-[#7C3AED] fill-[#7C3AED] shrink-0" />
                        ) : (
                          <Star className="w-3.5 h-3.5 text-white/20 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Custom Text (hidden for game card banner) */}
          {form.selectedStats.includes("custom_text") && !(cardType === "game" && form.style === "banner") && (
            <div>
              <label className="text-white/60 text-xs font-medium block mb-1.5">Custom Text / Motto</label>
              <Input
                value={form.customText}
                onChange={(e) => setForm((p) => ({ ...p, customText: e.target.value }))}
                placeholder="Achievement Hunter Extraordinaire"
                maxLength={80}
                className="bg-black/30 border-white/10 text-white text-sm"
                data-testid="custom-text-input"
              />
            </div>
          )}

          {/* Create / Save Button */}
          {editingCardId ? (
            <div className="flex gap-2">
              <Button
                onClick={handleSaveEdit}
                disabled={creating}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white font-medium"
                data-testid="save-edit-btn"
              >
                {creating ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
              <Button
                onClick={cancelEdit}
                variant="outline"
                className="border-white/10 text-white/60 hover:text-white"
                data-testid="cancel-edit-btn-bottom"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleCreate}
              disabled={creating || (cardType === "profile" && maxProfileReached)}
              className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-medium"
              data-testid="create-card-btn"
            >
              {creating ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              {cardType === "profile" && maxProfileReached
                ? `Limit Reached (${profileCount}/${options?.limits?.maxProfileCards})`
                : `Create ${cardType === "game" ? "Game" : "Profile"} Card`}
            </Button>
          )}
        </div>

        {/* Right: Preview */}
        <div className="lg:sticky lg:top-4 lg:self-start space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-white/60 text-xs font-medium">Preview</label>
              <button
                onClick={() => setUseSampleData(!useSampleData)}
                className={`flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-medium border transition-colors ${
                  useSampleData
                    ? "bg-amber-500/10 border-amber-500/25 text-amber-400"
                    : "bg-emerald-500/10 border-emerald-500/25 text-emerald-400"
                }`}
                data-testid="sample-data-toggle"
                title={useSampleData ? "Using sample data for fast preview. Click for live data." : "Using your real data. Click for sample data (faster)."}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${useSampleData ? "bg-amber-400" : "bg-emerald-400"}`} />
                {useSampleData ? "Sample Data" : "Live Data"}
              </button>
            </div>
            <div className="border border-white/10 rounded-lg bg-black/30 p-4 flex items-center justify-center min-h-[200px]" data-testid="preview-container">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Signature preview"
                  className="max-w-full h-auto"
                  data-testid="preview-image"
                  onError={() => {}}
                />
              ) : (
                <div className="text-center py-8 text-white/20 text-sm">
                  {cardType === "game"
                    ? "Select games and create a card to see preview"
                    : "Generating preview..."}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Existing Cards */}
      {cards.length > 0 && (
        <div data-testid="existing-cards-section">
          <h2 className="text-white font-heading font-bold text-lg mb-3">
            Your Signature Cards ({cards.length})
          </h2>
          <div className="space-y-3">
            {cards.map((card) => {
              const imgUrl = `${API}/signatures/img/${card.id}.png`;
              const siteUrl = "https://game-o-rang.com";
              const bbcode = `[url=${siteUrl}][img]${imgUrl}[/img][/url]`;
              const bbcodeSimple = `[url=${siteUrl}]${imgUrl}[/url]`;
              const html = `<a href="${siteUrl}"><img src="${imgUrl}" alt="${card.name}" /></a>`;
              const markdown = `[![${card.name}](${imgUrl})](${siteUrl})`;
              const expanded = showSnippets === card.id;

              return (
                <div key={card.id} className="border border-white/10 rounded-lg bg-black/20 overflow-hidden" data-testid={`card-row-${card.id}`}>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-white font-medium text-sm">{card.name}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${card.cardType === "game" ? "bg-emerald-500/10 text-emerald-400" : "bg-purple-500/10 text-purple-400"}`}>
                            {card.cardType}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/40">{card.style}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/40">{card.theme}</span>
                        </div>
                        <div className="text-white/30 text-xs">
                          {card.scope?.type?.replace(/_/g, " ")} | {card.selectedStats?.length} stats
                        </div>
                      </div>
                      <div className="flex gap-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-white/40 hover:text-amber-400"
                          onClick={() => editCard(card)}
                          data-testid={`edit-card-${card.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" />
                          <span className="text-xs">Edit</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-white/40 hover:text-white"
                          onClick={() => setShowSnippets(expanded ? null : card.id)}
                          data-testid={`toggle-snippets-${card.id}`}
                        >
                          <Copy className="w-3.5 h-3.5 mr-1" />
                          <span className="text-xs">Embed</span>
                          <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${expanded ? "rotate-180" : ""}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-white/40 hover:text-red-400"
                          onClick={() => handleDelete(card.id)}
                          data-testid={`delete-card-${card.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>

                    {/* Card image preview */}
                    <div className="mt-3 bg-black/40 rounded p-2 flex justify-center">
                      <img
                        src={`${imgUrl}?_t=${Date.now()}`}
                        alt={card.name}
                        className="max-w-full h-auto max-h-[200px]"
                        data-testid={`card-image-${card.id}`}
                      />
                    </div>
                  </div>

                  {/* Embed snippets */}
                  {expanded && (
                    <div className="border-t border-white/5 p-4 bg-black/30 space-y-3" data-testid={`snippets-${card.id}`}>
                      <SnippetBlock label="Direct Image URL" value={imgUrl} onCopy={copyToClipboard} />
                      <SnippetBlock label="BBCode (Forums)" value={bbcode} onCopy={copyToClipboard} />
                      <SnippetBlock label="BBCode (No [img] Tags)" value={bbcodeSimple} onCopy={copyToClipboard} />
                      <SnippetBlock label="HTML" value={html} onCopy={copyToClipboard} />
                      <SnippetBlock label="Markdown" value={markdown} onCopy={copyToClipboard} />

                      <a
                        href={imgUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-[#7C3AED] hover:text-[#A78BFA] transition-colors"
                        data-testid={`open-image-${card.id}`}
                      >
                        <ExternalLink className="w-3 h-3" />
                        Open image in new tab
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AdSense */}
      <AdBanner />
    </div>
    </ProGate>
  );
}

function SnippetBlock({ label, value, onCopy }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-white/50 text-xs font-medium">{label}</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-5 px-1.5 text-[10px] text-white/40 hover:text-white"
          onClick={() => onCopy(value)}
          data-testid={`copy-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
        >
          <Copy className="w-2.5 h-2.5 mr-1" /> Copy
        </Button>
      </div>
      <div className="bg-black/50 rounded p-2 text-[11px] text-white/60 font-mono break-all select-all border border-white/5">
        {value}
      </div>
    </div>
  );
}
